/**
 * Reusable AJAX "Add to Cart"
 * ---------------------------------------------------------------------------
 * ONE implementation, shared by every place the theme adds a product to the
 * cart: the product page (PDP), quick view, Frequently Bought Together,
 * collection product cards, product recommendations and any future
 * product-card component.
 *
 * A form opts in when it either:
 *   - has the class `product-form` (theme default markup), or
 *   - has a `data-ajax-cart-form` attribute (lightweight custom cards).
 *
 * Submits are captured with a single delegated listener on `document`, so
 * forms rendered after page load (quick view, recommendations, filtered
 * collection results, ...) work with no extra wiring.
 *
 * Programmatic use:
 *   window.ThemeAddToCart.addFromForm(formEl, { openDrawer: true });
 */
(function () {
  'use strict';

  var FORM_SELECTOR = 'form.product-form, form[data-ajax-cart-form]';
  var BUSY_CLASS = 'adding';

  function getCartAddUrl() {
    var routes = (window.MinimogSettings && window.MinimogSettings.routes) || {};
    var root =
      routes.root ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return (root + 'cart/add.js').replace('//cart/add.js', '/cart/add.js');
  }

  function getSubmitButton(form, submitter) {
    return (
      submitter ||
      form.querySelector('[type="submit"]:not([disabled])') ||
      form.querySelector('.add-to-cart, .bsp-card__add-button, [name="add"]')
    );
  }

  /**
   * Single source of truth for the ATC loading state.
   *
   * The ONLY visual mechanism is `.adding` on the form â€” exactly what the PDP
   * has always used. Theme CSS then reveals the `.atc-spinner` element that
   * lives inside every `.add-to-cart` button and hides the button's other
   * children. Product cards get the same spinner by rendering the shared
   * `atc-spinner` snippet inside their `.add-to-cart` button (see
   * snippets/product-card-like.liquid and snippets/product-card-custom.liquid).
   *
   * Do NOT add a second loader here (e.g. a `.loading` class â€” that triggers
   * the separate `button.loading::after` spinner in custom.css and you get two).
   */
  function setLoading(form, button, loading) {
    form.classList.toggle(BUSY_CLASS, loading);
    if (!button) return;
    button.disabled = loading;
    if (loading) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  function clearError(form) {
    var wrapper = form.querySelector('[data-error-message-wrapper]');
    if (wrapper) wrapper.classList.add('hidden');

    var inline = form.querySelector('.prod__form-error, [data-error-message]');
    if (inline) inline.textContent = '';

    var note = form.querySelector('.atc-error');
    if (note) note.remove();
  }

  function showError(form, button, message) {
    console.error('[add-to-cart]', message);

    // PDP / quick view / FBT already ship a dedicated error slot.
    var inline = form.querySelector('.prod__form-error, [data-error-message]');
    if (inline) {
      inline.textContent = message;
      var wrapper = inline.closest('[data-error-message-wrapper]');
      if (wrapper) wrapper.classList.remove('hidden');
      return;
    }

    // Compact product cards: append a lightweight, self-clearing message.
    var host = (button && button.closest('form')) || form;
    var note = host.querySelector('.atc-error');
    if (!note) {
      note = document.createElement('p');
      note.className = 'atc-error';
      note.setAttribute('role', 'alert');
      host.appendChild(note);
    }
    note.textContent = message;
    clearTimeout(note._hideTimer);
    note._hideTimer = setTimeout(function () {
      note.remove();
    }, 5000);
  }

  function refreshCart(item, openDrawer) {
    if (window.Shopify && typeof window.Shopify.onItemAdded === 'function') {
      window.Shopify.onItemAdded(item, openDrawer);
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      if (openDrawer) document.dispatchEvent(new CustomEvent('cart:open'));
    }
  }

  async function addFromForm(form, options) {
    options = options || {};
    if (!form || typeof form.matches !== 'function' || !form.matches(FORM_SELECTOR)) return;
    if (form.classList.contains(BUSY_CLASS)) return;

    var openDrawer = options.openDrawer !== false;
    var button = getSubmitButton(form, options.submitter);

    clearError(form);
    setLoading(form, button, true);

    try {
      var formData = new FormData(form);

      // Some third-party apps replace the native FormData; unwrap when needed.
      if (typeof formData._asNative === 'function') {
        formData = formData._asNative().fd;
      }

      var source =
        formData.get('source_event') || form.dataset.sourceEvent || 'product-form';

      var response = await fetch(getCartAddUrl(), {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      var data = await response.json();

      if (!response.ok || data.status) {
        throw data;
      }

      data.source = source;
      document.dispatchEvent(
        new CustomEvent('cart:item-added', { detail: { item: data, form: form } })
      );

      refreshCart(data, openDrawer);
      return data;
    } catch (err) {
      var message =
        (err && (err.description || err.message)) ||
        (window.MinimogStrings && window.MinimogStrings.cartError) ||
        'Sorry, this item could not be added to your cart.';
      showError(form, button, message);
      document.dispatchEvent(
        new CustomEvent('cart:error', { detail: { error: err, form: form } })
      );
    } finally {
      setLoading(form, button, false);
    }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

    event.preventDefault();
    addFromForm(form, { submitter: event.submitter });
  });

  window.ThemeAddToCart = {
    addFromForm: addFromForm,
    FORM_SELECTOR: FORM_SELECTOR,
  };
})();